import { useMemo, useState } from "react";
import { SecaoCard } from "@/components/padroes";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCents } from "@/lib/format";
import type { VariacaoItem } from "@/data/folha";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

interface Props {
  variacoes: VariacaoItem[];
}

const percentual = (v: number) =>
  `${v > 0 ? "+" : ""}${(v * 100).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%`;

export const ComparativoAnterior = ({ variacoes }: Props) => {
  const [limite, setLimite] = useState("30");

  const corte = Number(limite) / 100;
  const destacadas = useMemo(
    () =>
      variacoes.filter(
        (v) => v.saiuDaFolha || v.entrouAgora || (v.variacao !== null && Math.abs(v.variacao) >= corte),
      ),
    [variacoes, corte],
  );

  const semBase = variacoes.every((v) => v.liquidoAnterior === null);

  if (semBase) {
    return (
      <div className="rounded-xl border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
        Não há competência anterior para comparar. A partir do próximo mês esta aba mostra quem
        variou fora do esperado.
      </div>
    );
  }

  return (
    <SecaoCard
      semPadding
      titulo={`${destacadas.length} colaborador(es) fora do padrão`}
      descricao="Variação grande costuma ser erro de planilha — vírgula no lugar errado, comissão que não veio ou linha duplicada."
      acoes={
        <div className="flex items-center gap-2">
          <Label htmlFor="corte" className="text-xs font-normal text-muted-foreground">
            Destacar acima de
          </Label>
          <div className="flex items-center gap-1">
            <Input
              id="corte"
              type="number"
              min={0}
              max={500}
              value={limite}
              onChange={(e) => setLimite(e.target.value)}
              className="h-8 w-20"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>
      }
    >
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5 sm:pl-6">Colaborador</TableHead>
              <TableHead className="text-right">Mês anterior</TableHead>
              <TableHead className="text-right">Esta competência</TableHead>
              <TableHead className="pr-5 text-right sm:pr-6">Variação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {destacadas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center text-muted-foreground">
                  Ninguém variou acima de {limite}%. Nada suspeito nesta competência.
                </TableCell>
              </TableRow>
            ) : (
              destacadas.map((v) => {
                const subiu = (v.variacao ?? 0) > 0;
                return (
                  <TableRow key={v.colaboradorId} className={v.saiuDaFolha ? "bg-destructive/[.04]" : ""}>
                    <TableCell className="pl-5 font-medium sm:pl-6">
                      {v.nome}
                      {v.saiuDaFolha && (
                        <Badge variant="outline" className="ml-2 text-[10px] text-destructive">
                          Saiu da folha
                        </Badge>
                      )}
                      {v.entrouAgora && (
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          Entrou agora
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {v.liquidoAnterior === null ? "—" : formatCents(v.liquidoAnterior)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {v.saiuDaFolha ? "—" : formatCents(v.liquido)}
                    </TableCell>
                    <TableCell className="pr-5 text-right sm:pr-6">
                      {v.variacao === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 tabular-nums ${
                            subiu ? "text-amber-600 dark:text-amber-400" : "text-destructive"
                          }`}
                        >
                          {v.variacao === 0 ? (
                            <Minus className="h-3.5 w-3.5" />
                          ) : subiu ? (
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowDownRight className="h-3.5 w-3.5" />
                          )}
                          {percentual(v.variacao)}
                        </span>
                      )}
                      {v.liquidoAnterior !== null && !v.saiuDaFolha && v.liquido !== v.liquidoAnterior && (
                        <div className="text-xs text-muted-foreground">
                          {v.liquido > v.liquidoAnterior ? "+" : "−"}
                          {formatCents(Math.abs(v.liquido - v.liquidoAnterior))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
    </SecaoCard>
  );
};
